import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X, Coffee, UtensilsCrossed, PhoneOutgoing, GraduationCap, Users, ClipboardCheck, HelpCircle, MessageSquare, type LucideIcon } from 'lucide-react';
import { useSoftphone, type BreakReason } from '@/store/softphone';
import { Config } from '@/lib/api';

/**
 * Mola sebep seçim popover'ı. Topbar Coffee (Mola) butonunun altına anchor olur.
 *
 * Akış:
 *  - Agent molada DEĞİL + Coffee tıkla → bu popover açılır → sebep seçilir
 *    → triggerQueueAction('pause', code, reason) çağrılır → popover kapanır
 *  - Agent molada + Coffee tıkla → popover AÇILMAZ, direkt resume (Topbar logic)
 *
 * 3CX feature code aynı (*63) — sebep sadece activity_logs metadata'sında
 * raporlama için saklanır. İleride 3CX'in custom break codes desteği eklenirse
 * BREAK_REASONS map'ine spesifik code eklenebilir.
 *
 * 280px popover, 7 sebep button grid (1-col, tam genişlik tıklama target'i).
 */

interface ReasonSpec {
  key: BreakReason;
  label: string;
  Icon: LucideIcon;
  /** Ring/bg tonu — UI vurgu rengi. Operasyonel sebepleri renklerle ayırt et. */
  tone: 'amber' | 'orange' | 'sky' | 'violet' | 'red' | 'emerald' | 'slate' | 'brand';
  /** Agent kendi tarafından seçebilir mi? false = sadece takım lideri admin
   *  panelinden başkasına atayabilir (operasyonel kontrol). */
  agentAllowed: boolean;
}

export const BREAK_REASONS: ReasonSpec[] = [
  // ─── Agent self-service (sadece bu 3'ünü kendi seçebilir) ───
  { key: 'mola',          label: 'Kısa Mola',     Icon: Coffee,          tone: 'amber',   agentAllowed: true  },
  { key: 'yemek',         label: 'Yemek',         Icon: UtensilsCrossed, tone: 'orange',  agentAllowed: true  },
  { key: 'dis-arama',     label: 'Dış Arama',     Icon: PhoneOutgoing,   tone: 'brand',   agentAllowed: true  },
  // ─── Leader-assigned (sadece admin panelden başkasına verilebilir) ───
  { key: 'egitim',        label: 'Eğitim',        Icon: GraduationCap,   tone: 'violet',  agentAllowed: false },
  { key: 'toplanti',      label: 'Toplantı',      Icon: Users,           tone: 'sky',     agentAllowed: false },
  { key: 'sinav',         label: 'Sınav',         Icon: ClipboardCheck,  tone: 'red',     agentAllowed: false },
  { key: 'soru',          label: 'Soru Molası',   Icon: HelpCircle,      tone: 'emerald', agentAllowed: false },
  { key: 'geri-bildirim', label: 'Geri Bildirim', Icon: MessageSquare,   tone: 'slate',   agentAllowed: false },
];

const TONE_BG = {
  amber:   'bg-amber-50 text-amber-700 hover:bg-amber-100 ring-amber-200',
  orange:  'bg-orange-50 text-orange-700 hover:bg-orange-100 ring-orange-200',
  brand:   'bg-brand-50 text-brand-700 hover:bg-brand-100 ring-brand-200',
  sky:     'bg-sky-50 text-sky-700 hover:bg-sky-100 ring-sky-200',
  violet:  'bg-violet-50 text-violet-700 hover:bg-violet-100 ring-violet-200',
  red:     'bg-red-50 text-red-700 hover:bg-red-100 ring-red-200',
  emerald: 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100 ring-emerald-200',
  slate:   'bg-slate-50 text-slate-700 hover:bg-slate-100 ring-slate-200',
};

export function BreakReasonPopover() {
  const open = useSoftphone((s) => s.breakReasonOpen);
  const anchor = useSoftphone((s) => s.breakReasonAnchor);
  const close = useSoftphone((s) => s.closeBreakReasonPopover);
  const trigger = useSoftphone((s) => s.triggerQueueAction);
  const { data: pbxCfg } = useQuery({ queryKey: ['pbx-config'], queryFn: Config.pbx, enabled: open });

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const el = e.target as HTMLElement;
      if (!el.closest('[data-break-popover]') && !el.closest('[data-break-trigger]')) {
        close();
      }
    };
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    const id = setTimeout(() => {
      document.addEventListener('mousedown', onDocClick);
      document.addEventListener('keydown', onEsc);
    }, 0);
    return () => {
      clearTimeout(id);
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open, close]);

  if (!open) return null;

  const pick = (reason: BreakReason) => {
    if (!pbxCfg) {
      console.warn('[break] pbxCfg not loaded — abort');
      return;
    }
    trigger('pause', pbxCfg.queue_codes.pause, reason);
    close();
  };

  const positionStyle: React.CSSProperties = anchor
    ? { position: 'fixed', top: anchor.top, right: anchor.right, zIndex: 50 }
    : { position: 'fixed', top: 64, right: 20, zIndex: 50 };

  return (
    <div
      data-break-popover
      style={positionStyle}
      className="w-[280px] bg-surface rounded-2xl shadow-[0_18px_50px_-12px_rgba(15,23,42,0.25)] ring-1 ring-black/5 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-150"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3.5 py-2.5 border-b border-slate-100">
        <h3 className="text-[13px] font-semibold text-slate-900 tracking-tight flex items-center gap-1.5">
          <Coffee size={13} className="text-amber-500" /> Mola Sebebi
        </h3>
        <button onClick={close} className="p-1 text-slate-400 hover:text-slate-700 rounded">
          <X size={14} />
        </button>
      </div>

      {/* Reason list — sadece agentAllowed=true olanlar. Diğer sebepleri
           takım lideri admin panelinden başkasına atayabilir. */}
      <div className="p-2 flex flex-col gap-1">
        {BREAK_REASONS.filter((r) => r.agentAllowed).map((r) => {
          const cls = TONE_BG[r.tone];
          return (
            <button
              key={r.key}
              onClick={() => pick(r.key)}
              className={`flex items-center gap-2.5 px-3 py-2 rounded-lg ring-1 transition active:scale-[0.98] ${cls}`}
            >
              <r.Icon size={16} className="flex-shrink-0" />
              <span className="text-[13px] font-medium">{r.label}</span>
            </button>
          );
        })}
      </div>

      <div className="px-3.5 pb-2.5 text-[10.5px] text-slate-400">
        Eğitim/toplantı/sınav türleri takım liderinden talep edilir
      </div>
    </div>
  );
}

/** Mola sebep key'ini display label'a çevir — activity feed gibi başka
 *  bileşenlerin sebep göstermesi için. */
export function reasonLabel(reason: BreakReason | null | undefined): string {
  if (!reason) return '';
  const spec = BREAK_REASONS.find((r) => r.key === reason);
  return spec?.label ?? reason;
}
